import { $http } from "@/plugins/api";
import { Commit } from "vuex";
import {
  IncidentSummary,
  IncidentDetails,
  ReceivedHeader,
  RecipientDetails,
} from "../types/IncidentSummary";
import { Params } from "@/types/api";
import { IncidentAction } from "../views/Common/Incidents/Common/IncidentList.types";

interface IncidentSummaryState {
  incidentSummary: IncidentSummary | null;
  incidentDetails: IncidentDetails | null;
  receivedHeaders: ReceivedHeader[];
  recipientsDetails: RecipientDetails[];
}
type IncidentActionPayload = {
  globalOrganisationID: string;
  internetMessageID: string;
  mailSpMessageID: string;
};

type HeaderActionPayload = {
  customerGlobalAccountID: string;
  mailbox: string;
  mailSpMessageID: string;
};

const state: IncidentSummaryState = {
  incidentSummary: null,
  incidentDetails: null,
  receivedHeaders: [],
  recipientsDetails: [],
};

const getters = {
  incidentSummary: (state: IncidentSummaryState) => state.incidentSummary,
  incidentDetails: (state: IncidentSummaryState) => state.incidentDetails,
  receivedHeaders: (state: IncidentSummaryState) => state.receivedHeaders,
  recipientsDetails: (state: IncidentSummaryState) => state.recipientsDetails,
};

const actions = {
  async getIncidentDetails(
    { commit }: { commit: Commit },
    {
      globalOrganisationID,
      internetMessageID,
      mailSpMessageID,
    }: IncidentActionPayload,
  ) {
    commit("setIncidentDetails", null);
    const encodedMailSpMessageID = encodeURIComponent(mailSpMessageID);

    const res = await $http.get(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/history/${globalOrganisationID}/incidents/${internetMessageID}/${encodedMailSpMessageID}/details`,
      {
        _hasCustomErrorHandling: true,
      },
    );

    commit("setIncidentDetails", res.data);
  },
  async getRecipientsDetails(
    { commit }: { commit: Commit },
    {
      globalOrganisationID,
      internetMessageID,
    }: { globalOrganisationID: string; internetMessageID: string },
  ) {
    commit("setRecipientsDetails", []);

    const res = await $http.get(
      `${import.meta.env.VITE_API_CLIENT}/v3/pt/history/recipients/${globalOrganisationID}/${internetMessageID}`,
    );

    commit("setRecipientsDetails", res.data);
  },
  async getReceivedHeaders(
    { commit }: { commit: Commit },
    { customerGlobalAccountID, mailbox, mailSpMessageID }: HeaderActionPayload,
  ) {
    const res = await $http.get(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/mail-message/${customerGlobalAccountID}/${mailbox}/${mailSpMessageID}/headers`,
    );

    commit("setReceivedHeaders", res.data);
  },
  async updateIncidentStatus(
    { commit, state }: { commit: Commit; state: IncidentSummaryState },
    {
      globalOrganisationID,
      ingressID,
      status,
      params,
    }: {
      globalOrganisationID: string;
      ingressID: string;
      status: IncidentAction;
      params: Params;
    },
  ) {
    const searchParams = new URLSearchParams(params);

    const res = await $http.put(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v2/pt/msg/${globalOrganisationID}/incidents/${ingressID}/status?${searchParams.toString()}`,
      {
        status,
      },
    );

    commit("setIncidentSummary", {
      ...state.incidentSummary,
      status:
        res.data.status === "junk"
          ? "AdminRemediated_Phish"
          : "AdminRemediated_NonPhish",
    });
    return res.data.data;
  },
};

const mutations = {
  setIncidentDetails: (state: IncidentSummaryState, data: IncidentDetails) =>
    (state.incidentDetails = data),
  setIncidentSummary: (state: IncidentSummaryState, data: IncidentSummary) =>
    (state.incidentSummary = data),
  setRecipientsDetails: (
    state: IncidentSummaryState,
    data: RecipientDetails[],
  ) => (state.recipientsDetails = data),
  setReceivedHeaders: (state: IncidentSummaryState, data: ReceivedHeader[]) =>
    (state.receivedHeaders = data),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
